import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { orderService, addreessesService } from 'services'
import { IOrder } from 'config/interfaces'
import { useWebSocket } from 'hooks'
import { useSession } from 'context/SessionContext'
import { useError } from 'hooks'

export default function useOrder(orderId?: string) {
  const { verifyError } = useError()
  const [loading, setLoading] = useState(false)
  const [loadingUpdate, setLoadingUpdate] = useState(false)
  const [orders, setOrders] = useState([] as IOrder.Order[])
  const [selectedRows, setSelectedRows] = useState([] as Array<IOrder.Order>)
  const [errorMessage, setErrorMessage] = useState('')
  const { newMessage: newOrder } = useWebSocket({
    subscribe: '/topic/frontend.transaction.all'
  })
  const { IDMCM } = useSession()

  const mountOrders = (orders: IOrder.Order[], status: number) => {
    if (status !== 204) {
      const array = orders.map((order) => {
        return {
          ...order,
          DHFIM: order.DHFIM ? order.DHFIM : '0000-00-00T00:00:00.000Z'
        }
      })
      setOrders(array)
    } else {
      setOrders([] as IOrder.Order[])
    }
  }

  useEffect(() => {
    setLoading(true)
    if (!orderId) {
      ;(async () => {
        try {
          const { data, status } = await orderService.getAll({ IDMCM })

          mountOrders(data, status)
          // status 204 = No Content
        } catch (error: any) {
          verifyError(error.response.status)
          setErrorMessage(error?.response?.data?.message)
        } finally {
          setLoading(false)
        }
      })()
    } else {
      ;(async () => {
        try {
          const { data } = await orderService.getAll({
            OFFSET: 0,
            LIMIT: 20,
            IDTRANSACAO: Number(orderId)
          })
          setOrders(data)
          setLoading(false)
        } catch (error: any) {
          verifyError(error.response.status)
          setErrorMessage(error?.response?.data?.message)
        } finally {
          setLoading(false)
        }
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IDMCM])

  // useEffect(() => {
  //   (async () => {
  //     try {
  //       const { data, status } = await orderService.getAll({ IDMCM });

  //       if (status !== 204) setOrders(data); // status 204 = No Content
  //     } catch (error: any) {
  //       setErrorMessage(error?.response?.data?.message);
  //     } finally {
  //       setLoading(false);
  //     }
  //   })();
  // });

  useEffect(() => {
    if (newOrder) {
      const order: IOrder.Order = newOrder as IOrder.Order

      const isNew = !orders.some((x) => x.IDTRANSACAO === order.IDTRANSACAO)
      if (isNew) {
        setOrders([newOrder, ...orders])
      } else {
        const newOrders = [...orders]
        const index = orders.findIndex(
          (x) => x.IDTRANSACAO === order.IDTRANSACAO
        )
        newOrders[index] = newOrder
        setOrders(newOrders)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOrder])

  const statusOptions = [
    {
      label: 'AGUARDANDO RETIRADA',
      value: '0'
    },
    {
      label: 'EXPIRADA',
      value: '4'
    },
    {
      label: 'RETIRADA',
      value: '5'
    },
    {
      label: 'AGUARDANDO FECHAMENTO DA PORTA',
      value: '7'
    },
    {
      label: 'PENDENTE',
      value: '8'
    },
    {
      label: 'CANCELADA',
      value: '9'
    },
    {
      label: 'CANCELADA',
      value: '12'
    },
    {
      label: 'COLETADA',
      value: '13'
    }
  ]

  async function getAllOrders(params?: IOrder.Params) {
    setLoading(true)
    try {
      const { data, status } = await orderService.getAll({ ...params, IDMCM })
      mountOrders(data, status)
      //   return setOrders([] as IOrder.Order[]);

      // setOrders(data);
    } catch (error: any) {
      verifyError(error.response.status)
      setErrorMessage(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }

  async function setPagination(OFFSET: number, LIMIT: number) {
    try {
      setLoading(true)
      const { data } = await orderService.getAll({
        OFFSET: OFFSET > 0 ? OFFSET : 0,
        LIMIT: 1000
      })
      setOrders(data)
    } catch (error: any) {
      verifyError(error.response.status)
      setErrorMessage(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }

  async function getOrdersByFilters(params: IOrder.Params) {
    setLoading(true)
    try {
      const { data, status } = await orderService.getAll({ ...params, IDMCM })

      mountOrders(data, status)
    } catch (error: any) {
      verifyError(error.response.status)
      setErrorMessage(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }

  async function updateStatusOrder(orders: IOrder.Order[], command: number) {
    // const ids = orders.map(order => order.IDTRANSACAO);
    // const values = ids.map(id => {
    //   return {
    //     idtransacao: Number(id),
    //     command
    //   }
    // });
    setLoadingUpdate(true)
    orders.forEach((order) => {
      const values = {
        idtransacao: order.IDTRANSACAO,
        command
      }
      orderService
        .updateStatus(values)
        .then((resp) => {
          if (command === 0)
            toast.success('Solicitação de confirmação enviada.')
          if (command === 1)
            toast.success('Solicitação de cancelamento enviada.')
        })
        .catch((error) => {
          verifyError(error.response.status)
          if (command === 0) toast.error('Falha na regularização da entrega.')
          if (command === 1) toast.error('Falha no cancelamento da entrega.')
        })
    })

    setLoadingUpdate(false)
  }

  interface RenewCredential {
    IDTRANSACAO: string | number
    OBS: string
  }
  async function renewCredential(data: RenewCredential) {
    try {
      await addreessesService.renewCredential(data)
      toast.success('Credencial renovada!')
    } catch (error: any) {
      toast.error('Não foi possível renovar a credencial!')
    }
  }

  return {
    loading,
    orders,
    errorMessage,
    setPagination,
    updateStatusOrder,
    renewCredential,
    getAllOrders,
    statusOptions,
    getOrdersByFilters,
    selectedRows,
    setSelectedRows,
    loadingUpdate,
    IDMCM
  }
}
